import {
  AdgroupConfig,
  AdgroupFormData,
  BrandSafetyType,
  CampaignFormData,
  REGION_ID,
  ExtractRequestFn,
  CreateMode,
  LocationLevel,
  LocationTargetingScene,
  LocationGeoType,
  AreaItem,
  CloverAreaIds,
  TtpAreaIds,
  AreaStatus,
  FORM_ITEM_ELEMENT_ID,
} from '@tt4b/ttam-enums';
import {
  ValidationError,
  useDSAAssignInfoApi,
  useRequest,
  useStdObjectiveType,
  useStore,
  NetStateEnum,
  StateEnum,
  useAdgroupMode,
  useStdAccountStore,
  useLocationInfoBffApi,
  useAdgroupLocationAvailable,
  useAllowlist,
  useStdProductSales,
  useStdAdgroupIs,
  useCatalogStore,
  useShopStore,
  useSharedAreaStore,
  useFeatureGatingUserTags,
} from '@tt4b/ttam-hooks';

import { getCurrentLang, transformLocations, splitActiveIds } from '@tt4b/ttam-utils';

import { computed, Ref, ref } from '@vue/composition-api';
import { i18n } from './../../../../i18n';
import {
  transformSelectedIds,
  generateLocationTargetingAdContext,
  formatLocationItem,
  getTtpCloverAreaInfo,
} from '../utils/index';
import { cloneDeep, difference, intersection, isEmpty, uniq } from 'lodash-es';
import TreeStore from 'byted-vui/packages/tree/src/model/tree-store';
import { useLocationTargetingDataSource } from './location-data-source';
import { MAX_COUNT } from '../const';
export * from './bulk-upload';
export * from './search';
export * from './undo-current-changes';
export * from './location-data-source';

export function usePropsBrandSafetyTipVisible(options: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  createMode: Ref<CreateMode>;
  isBsEditable: Ref<boolean>;
}) {
  const { campaignData, adgroupData, createMode, isBsEditable } = options;
  const { isEdit } = useAdgroupMode({
    createMode,
  });
  const { isProductSales } = useStdObjectiveType({ campaignFormData: campaignData });
  const brandSafetyTipVisible = computed(
    () =>
      isEdit.value &&
      (![BrandSafetyType.UN_SAFE, BrandSafetyType.EXPANDED_INVENTORY].includes(adgroupData.value.brandSafety) ||
        adgroupData.value.suitabilityNonGarmCategory.length > 0) &&
      !isBsEditable.value,
  );
  const brandSafetyTipContent = computed(() => {
    if (isProductSales.value) {
      return i18n.t('edit_location_disable_tips_content_exclusion');
    }

    const content: string[] = [];
    if (![BrandSafetyType.UN_SAFE, BrandSafetyType.EXPANDED_INVENTORY].includes(adgroupData.value.brandSafety)) {
      content.push(i18n.t('module_adgroup_placement_new_bs_inventory_filter') as string);
    }
    if (adgroupData.value.suitabilityNonGarmCategory.length) {
      content.push(i18n.t('module_category_exclusion_text') as string);
    }
    if (content.length) {
      return i18n.t('edit_location_disabled_tips_brand_safety', {
        content: content.join(', '),
      }) as string;
    } else {
      return '';
    }
  });
  return {
    brandSafetyTipVisible,
    brandSafetyTipContent,
  };
}

export const useAnchorTipsVisibly = (adgroupData: Ref<AdgroupFormData>, createMode: Ref<CreateMode>) => {
  const { isEdit } = useAdgroupMode({
    createMode,
  });
  const anchorTipContent = computed(() => {
    if (adgroupData.value.isBrandUseProductAnchor && isEdit.value) {
      return i18n.t('module_adgroup_anchor_location_disable_tip');
    }
    return '';
  });
  return {
    anchorTipContent,
  };
};

export function useTreeProps(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = opt;
  const { isNewLocationAvailable } = useAdgroupLocationAvailable();
  const treeProps = ref({
    label: 'translation',
    filterLabel: 'filterLabel',
  });

  const treeResultListType = computed(() => {
    if (isNewLocationAvailable.value) {
      return 'none';
    }
    return 'auto-outside';
  });

  const filterCheckStrictly = computed(() => isNewLocationAvailable.value);

  function selectTreeFilterMethod(options) {
    if (isNewLocationAvailable.value) {
      return () => true;
    }
    const { treeProps: _treeProps, caseSensitive = false } = options;
    return (keyword, node) => {
      if (!keyword) {
        return true;
      }
      let label = `${node[_treeProps.label]}`;
      if (!caseSensitive) {
        label = label.toLowerCase();
        keyword = keyword.toLowerCase();
      }
      return label.includes(keyword);
    };
  }

  /** particleLocation + zipcodeIds 最多可选MAX_COUNT个 */
  const enableSelectOneNode = computed(() => {
    if (isNewLocationAvailable.value) {
      const { particleLocations, zipcodeIds } = adgroupData.value;
      return particleLocations.length + zipcodeIds.length < MAX_COUNT;
    }
    return true;
  });

  return {
    treeProps,
    treeResultListType,
    filterCheckStrictly,
    selectTreeFilterMethod,
    enableSelectOneNode,
  };
}

export function usePropsDisabledLocation(opt: {
  adgroupData: Ref<AdgroupFormData>;
  adgroupConfig: Ref<AdgroupConfig>;
}) {
  const disabledLocation = computed(() => opt.adgroupConfig.value.disableLocation);
  return {
    disabledLocation,
  };
}

/**
 * 编辑判断国家是否已经不再可能有效
 * 如果之前选择了 US or Clover 地区，并且已经不存在可选的，则无法通过校验，广告已经无效
 * @param opt
 * @returns
 */
export function useTtpCloverLocationValidStatus(opt: { originAdgroupData: Ref<AdgroupFormData> }) {
  const { originAdgroupData } = opt;
  const { areaMap } = useLocationTargetingDataSource();
  const isTtpCloverLocationInvalid = computed(() => {
    const { isOriginHasCloverArea, isOriginHasTtpArea } = getTtpCloverAreaInfo(originAdgroupData.value?.country);
    const isExistingValidLocation = list => {
      // 如果获取不到状态（例如接口失败，返回慢）不处理
      if (isEmpty(areaMap.value)) {
        return true;
      }
      return list.some(id => {
        const area = areaMap.value[id];
        return isEmpty(area) ? true : area.status === AreaStatus.ACTIVE;
      });
    };
    const isExistingValidCloverArea = isExistingValidLocation(CloverAreaIds);
    const isExistingValidTtpArea = isExistingValidLocation(TtpAreaIds);

    if (!isOriginHasCloverArea && !isOriginHasTtpArea) {
      return false;
    } else if (isOriginHasCloverArea && isOriginHasTtpArea) {
      return !isExistingValidCloverArea || !isExistingValidTtpArea;
    } else if (isOriginHasCloverArea) {
      return !isExistingValidCloverArea;
    } else {
      return !isExistingValidTtpArea;
    }
  });

  return {
    isTtpCloverLocationInvalid,
  };
}

/**
 * 获取资产(catalog, shop) 绑定的国家的下线状态
 * @param opt
 * @returns
 */
export function useAssetLocationValidSattus(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { campaignData, adgroupData } = opt;
  const { isStandardAdsWithCatalog, isStandardAdsWithShop } = useStdAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });
  const { isCatalogCountryDisabled } = useCatalogStore({ adgroupFormData: adgroupData });
  const { isShopCountryDisabled } = useShopStore({ adgroupFormData: adgroupData });

  const isAssetLocationInvalid = computed(() => {
    const catalogCountryDisabled = isStandardAdsWithCatalog.value && isCatalogCountryDisabled.value;
    const shopCounrtyDisabled = isStandardAdsWithShop.value && isShopCountryDisabled.value;

    return catalogCountryDisabled || shopCounrtyDisabled;
  });

  return {
    isAssetLocationInvalid,
  };
}

export function useRules(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  originAdgroupData?: Ref<AdgroupFormData>;
  createMode?: Ref<CreateMode>;
}) {
  const { adgroupData, campaignData, originAdgroupData, createMode } = opt;
  const { areaMap } = useLocationTargetingDataSource();
  const { isEdit } = useAdgroupMode({ createMode: createMode ?? computed(() => CreateMode.CREATE) });
  const { isHasCloverWhite } = useAllowlist();

  const { isShowcaseSource } = useStdProductSales({ adgroupFormData: opt.adgroupData });
  const validateTtpCloverAreaCleared = (country: number[]) => {
    const { isOriginHasCloverArea, isOriginHasTtpArea, isCurrentHasCloverArea, isCurrentHasTtpArea } =
      getTtpCloverAreaInfo(originAdgroupData?.value?.country, country);
    // 把 Clover 地区的全删了
    const isCloverAreaCleared = isHasCloverWhite.value && isOriginHasCloverArea && !isCurrentHasCloverArea;
    // 把 TTP 地区的全删了，ttp的校验逻辑已全量
    const isTtpAreaCleared = isOriginHasTtpArea && !isCurrentHasTtpArea;

    let messageI18nKey = '';

    if (isCloverAreaCleared && isTtpAreaCleared) {
      messageI18nKey = 'adgroup_edit_location_clear_clover_and_us_error_tip';
    } else if (isCloverAreaCleared) {
      messageI18nKey = 'adgroup_edit_location_clear_clover_error_tip';
    } else if (isTtpAreaCleared) {
      messageI18nKey = 'adgroup_edit_location_clear_us_error_tip';
    }
    return messageI18nKey;
  };

  return {
    locationValidator: computed(() => [
      {
        validator: (rule, value: number[], callback, data: AdgroupFormData) => {
          const { particleLocations, zipcodeIds } = data;
          const ids = [...particleLocations, ...zipcodeIds.map(id => areaMap.value[id]?.info?.geo?.parent_id)];
          const locationParams = transformLocations({
            particleLocations: ids,
            locationParams: {
              areaMap: areaMap.value,
              isSplitTestBidding: false,
              isSplitTestCreative: false,
            },
          });
          const country = (locationParams.country as number[]) ?? [];
          if (!particleLocations.length && !zipcodeIds.length) {
            return callback(
              new ValidationError({
                message: i18n.t('adgroup_district_hint'),
                messageI18nKey: 'adgroup_district_hint',
                moduleName: i18n.t('adgroup_audience_target'),
                subModuleName: i18n.t('adgroup_district'),
                moduleId: FORM_ITEM_ELEMENT_ID.LOCATION,
              }),
            );
          }
          // 品牌安全支持一个国家
          if (isShowcaseSource.value && country?.length > 1) {
            return callback(
              new ValidationError({
                message: i18n.t('module_location_single_country_error'),
                messageI18nKey: 'module_location_single_country_error',
                moduleName: i18n.t('adgroup_audience_target'),
                subModuleName: i18n.t('adgroup_district'),
                moduleId: FORM_ITEM_ELEMENT_ID.LOCATION,
              }),
            );
          }

          // 用户在修改的时候不能删掉 Clover 地区或者 US
          if (isEdit.value) {
            const messageI18nKey = validateTtpCloverAreaCleared(country);

            if (messageI18nKey) {
              return callback(
                new ValidationError({
                  message: i18n.t(messageI18nKey),
                  messageI18nKey,
                  moduleName: i18n.t('adgroup_audience_target'),
                  subModuleName: i18n.t('adgroup_district'),
                  moduleId: 'targetingLocation',
                }),
              );
            }
          }

          const disabledArea = ids.map(id => areaMap.value[id]).filter(area => area?.disabled);
          if (disabledArea.length) {
            const areaName = disabledArea.map(area => area.translation).join(',');
            return callback(
              new ValidationError({
                message: `${i18n.t('adgroup_disabled_area_hint')}(${areaName})`,
                messageI18nKey: 'adgroup_disabled_area_hint',
                moduleName: i18n.t('adgroup_audience_target'),
                subModuleName: i18n.t('adgroup_district'),
                moduleId: 'targetingLocation',
              }),
            );
          }

          // 校验国家和 CA 选择的商品库是否匹配
          // const { validateLocationForCA } = useValidatorCollectionAds();
          // if (!validateLocationForCA(rule, country, callback)) {
          //   return;
          // }
          return callback();
        },
        trigger: 'custom',
      },
    ]),
  };
}

export function useDSA() {
  const { data: DSAInfo } = useRequest(useDSAAssignInfoApi);
  const DSAHref = computed(() => `/cg/contract_creation/contract/dsa/?register_code=${DSAInfo?.value?.token}`);
  const urlSignLink = computed(() => `/cg/contract_creation/contract/dsa/?register_code=${DSAInfo?.value?.token}`);
  const DSALoading = ref(false);

  const openDSAClick = () => {
    DSALoading.value = true;
    try {
      // DOM
      const a = document.createElement('a');
      a.href = urlSignLink.value;
      a.setAttribute('target', 'blank');
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      DSALoading.value = false;
    }
  };

  return {
    DSAHref,
    DSAInfo,
    DSALoading,
    openDSAClick,
  };
}

export function usePropsDSATipsVisible(opt: {
  adgroupData: Ref<AdgroupFormData>;
  DSAInfo: Ref<ExtractRequestFn<typeof useDSAAssignInfoApi>>;
}) {
  const { isSelfAccount } = useStdAccountStore();
  const { areaMap } = useLocationTargetingDataSource();
  const { adgroupData, DSAInfo } = opt;
  const DSAIds = [REGION_ID.FRANCE, REGION_ID.ITALY, REGION_ID.UNITED_KINGDOM, REGION_ID.SPAIN, REGION_ID.POLAND];
  /** 实际选中的国家 zipcode无国家*/
  const countryIds = computed(() =>
    adgroupData.value.particleLocations.filter(item => areaMap.value[item]?.level === 1),
  );

  const dsaTipsVisible = computed(
    () =>
      isSelfAccount.value &&
      DSAInfo.value?.permitted_to_sign &&
      !DSAInfo.value?.signed &&
      countryIds.value.some(regionId => DSAIds.includes(regionId)),
  );
  return {
    dsaTipsVisible,
  };
}

// area_v2接口返回的tree list
export function useDataSource(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
  filterMethod?: Ref<(param: AreaItem[]) => AreaItem[]>;
}) {
  const { adgroupData, campaignData, filterMethod } = opt;
  const { objectiveType } = campaignData.value;
  const { inventoryFlow, platform, brandSafety, brandSafetyPartner, classify, externalType, promotionTargetType } =
    adgroupData.value;
  const { getStore } = useStore();
  const { isUsCityLocationAllAvailable } = useAdgroupLocationAvailable();

  const areaParam = {
    classify,
    platform,
    objectiveType,
    inventoryFlow,
    brandSafety: brandSafety === BrandSafetyType.FLAGSHIP ? brandSafetyPartner && brandSafety : brandSafety,
    brandSafetyPartner,
    currentLang: getCurrentLang(),
    externalType,
    promotionTargetType,
    level: isUsCityLocationAllAvailable.value ? LocationLevel.PROVINCE : LocationLevel.ALL,
  };
  const { data, isLoading, run: loadData } = getStore(NetStateEnum.areaData, areaParam);
  const { getArea } = useSharedAreaStore();

  const areaMap = computed(() => data.value?.areaMap);
  const metroIdsMap = computed(() => data.value?.metroIdsMap);
  const regionIdsMap = computed(() => data.value?.regionIdsMap);
  const virtualNodeIdArr = computed(() => data.value?.virtualNodeIdArr);
  const virtualNodeIdParentIdMap = computed(() => data.value?.virtualNodeIdParentIdMap);
  /**
   * 使用 getArea()?.value.areaList 替换 data.value.areaListForLocationTree (等价的)
   * 因为 data 修改 location 会触发 computed，导致 areaList 重新执行，从而导致 availableAreaList 重新执行
   * 当有 filterMethod 会返回新的 list ，导致页面列表被收起，影响体验
   */
  const areaList = computed(() => getArea().value.areaList ?? []);
  // 在电商场景下会过滤出商品库对应的国家
  const availableAreaList = computed(() =>
    typeof filterMethod.value === 'function' ? filterMethod.value(areaList.value) : areaList.value,
  );

  return {
    data,
    loadData,
    isLoading,
    areaList,
    areaMap,
    availableAreaList,
    virtualNodeIdArr,
    virtualNodeIdParentIdMap,
    metroIdsMap,
    regionIdsMap,
  };
}

export function useSetOfflineArea(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = opt;
  const { allUnsupportedLocationIds, updateOfflineAreaTipsVisibleState } = useLocationTargetingDataSource();
  const splitLocationOffline = () => {
    const { activeIds: activeParticleLocations, offlineIds: offlineParticleLocations } = splitActiveIds(
      adgroupData.value.particleLocations,
      allUnsupportedLocationIds.value,
    );

    const { activeIds: activeZipcodeIds, offlineIds: offlineZipcodeIds } = splitActiveIds(
      adgroupData.value.zipcodeIds,
      allUnsupportedLocationIds.value,
    );
    const offlineLocations = [...offlineParticleLocations, ...offlineZipcodeIds];
    const hasOfflineLocation = Boolean(offlineLocations.length);
    updateOfflineAreaTipsVisibleState(hasOfflineLocation);

    return {
      hasOfflineLocation,
      offlineLocations,
      activeParticleLocations,
      activeZipcodeIds,
    };
  };

  return {
    splitLocationOffline,
  };
}

export function usePropsOfflineAreaTipsVisible(opt: {
  campaignData: Ref<CampaignFormData>;
  adgroupData: Ref<AdgroupFormData>;
  createMode: Ref<CreateMode>;
  originAdgroupData: Ref<AdgroupFormData>;
}) {
  const { campaignData, adgroupData, createMode, originAdgroupData = ref({} as AdgroupFormData) } = opt;
  const { updateOfflineAreaTipsVisibleState } = useLocationTargetingDataSource();
  const { state } = useStore();
  const { isEdit } = useAdgroupMode({ createMode });
  const { isAssetLocationInvalid } = useAssetLocationValidSattus({ campaignData, adgroupData });
  const { isTtpCloverLocationInvalid } = useTtpCloverLocationValidStatus({ originAdgroupData });
  const { isProductSales } = useStdObjectiveType({ campaignFormData: campaignData });
  const { hasSamCountryRestrictTag } = useFeatureGatingUserTags();

  const isSamCountryRestrict = computed(() => hasSamCountryRestrictTag.value && !isProductSales.value);

  const offlineAreaTipsVisible = computed(() => {
    const areaData = state[StateEnum.areaData];
    const isShowAlert = isEdit.value && (isTtpCloverLocationInvalid.value || isAssetLocationInvalid.value);

    // SAM广告主非Product sales投放国家限制提示，复制/编辑/草稿
    if (isSamCountryRestrict.value) {
      return areaData.data.value.hasSelectedOfflineAreas;
    }
    return areaData.data.value.hasSelectedOfflineAreas && !isShowAlert;
  });

  const offlineAreaTips = computed(() => {
    if (!offlineAreaTipsVisible.value) {
      return '';
    }
    console.log('wx isSamCountryRestrict', hasSamCountryRestrictTag.value, isProductSales.value);
    if (offlineAreaTipsVisible.value && isSamCountryRestrict.value) {
      return i18n.t('module_adgroup_region_offline_tips_sam');
    }
    return i18n.t('module_adgroup_region_offline_tips');
  });

  return {
    offlineAreaTipsVisible,
    updateOfflineAreaTipsVisibleState,
    offlineAreaTips,
  };
}

export function useLocationSelectedList(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const { areaMap } = useLocationTargetingDataSource();

  const transformSelectedIdsResult = computed(() =>
    transformSelectedIds({
      ids: [...adgroupData.value.particleLocations, ...adgroupData.value.zipcodeIds],
      areaMap: areaMap.value,
    }),
  );
  const selectedList = computed(() => Object.values(transformSelectedIdsResult.value.selectedList || {}));

  function handleSingleTagDelete(val) {
    const { particleLocations, zipcodeIds } = adgroupData.value;
    const particleLocationsSet = new Set(particleLocations);
    particleLocationsSet.delete(val.id);

    const zipcodeIdsSet = new Set(zipcodeIds);
    zipcodeIdsSet.delete(val.id);
    return {
      zipcodeIdsSet,
      particleLocationsSet,
    };
  }

  const selectedlocationsName = computed(() => transformSelectedIdsResult.value.names);
  return {
    selectedList,
    handleSingleTagDelete,
    transformSelectedIdsResult,
    selectedlocationsName,
  };
}

export function usePropsLocationName(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const { selectedlocationsName } = useLocationSelectedList({ adgroupData });

  return {
    selectedlocationsName,
  };
}

export function useStdLocation(options: { adgroupData: Ref<AdgroupFormData> }) {
  const { adgroupData } = options;
  const { state } = useStore();
  const { areaMap, areaList } = useLocationTargetingDataSource();
  const { selectedlocationsName } = usePropsLocationName({ adgroupData });

  const accountInfo = computed(() => state[StateEnum.accountInfo].data.value);
  const accountCountry = computed(() => accountInfo.value.account.country);

  const transformResult = computed(() =>
    transformLocations({
      particleLocations: adgroupData.value.particleLocations,
      zipcodeIds: adgroupData.value.zipcodeIds,
      locationParams: {
        areaMap: areaMap.value,
        isSplitTestBidding: false,
        isSplitTestCreative: false,
      },
    }),
  );

  /** 实际选中的国家 */
  const countryIds = computed(() =>
    adgroupData.value.particleLocations.filter(item => areaMap.value?.[item]?.level === 1),
  );
  const countryCodes = computed(() =>
    countryIds.value.map(id => areaMap.value[id ?? '']?.code || '').filter(item => item),
  );
  const provinceIds = computed(() =>
    adgroupData.value.particleLocations.filter(item => areaMap.value[item]?.level === 2),
  );
  const cityIds = computed(() => adgroupData.value.particleLocations.filter(item => areaMap.value[item]?.level === 3));

  /** 全量的国家范围（包含选了city未选country的国家） */
  const fullCountryIds = computed<number[]>(() => transformResult.value.country ?? []);
  const fullCountryCodes = computed<string[]>(() =>
    fullCountryIds.value.map(id => areaMap.value[id ?? '']?.code || '').filter(item => item),
  );
  const fullProvinceIds = computed(() => transformResult.value.province ?? []);
  const fullCityIds = computed(() => transformResult.value.city ?? []);

  const registerCountryId = computed(() => {
    const countryItem = areaList.value?.find(item => item.code === accountCountry.value);
    return countryItem?.id;
  });

  const getLocationNameFromId = ids => ids.map(id => areaMap.value[id]?.translation);
  const getLocationNameFromShortName = names =>
    names.map(name => {
      const id = Object.keys(areaMap.value).filter(v => areaMap.value[v].code === name)[0];
      return areaMap.value[id]?.translation;
    });

  const transformParticleLocationsResult = computed(() =>
    transformLocations({
      particleLocations: adgroupData.value.particleLocations,
      locationParams: {
        areaMap: areaMap.value,
        isSplitTestBidding: false,
        isSplitTestCreative: false,
      },
    }),
  );
  // reach-forecast 本期不支持zipcode 推导国家,支持后删除
  const particleLocationsCountryIds = computed<number[]>(() => transformParticleLocationsResult.value.country ?? []);

  return {
    countryIds,
    countryCodes,
    provinceIds,
    cityIds,
    fullCountryIds,
    fullCountryCodes,
    fullProvinceIds,
    fullCityIds,
    registerCountryId,
    getLocationNameFromId,
    getLocationNameFromShortName,
    selectedlocationsName,
    particleLocationsCountryIds,
  };
}

export function useLocationListInfoDataSource(opt: {
  adgroupData: Ref<AdgroupFormData>;
  campaignData: Ref<CampaignFormData>;
}) {
  const { updateLocationData } = useLocationTargetingDataSource();
  const { adgroupData, campaignData } = opt;

  const { run } = useRequest(useLocationInfoBffApi, {} as any, {
    manual: true,
  });

  async function getLocationListInfo() {
    const { particleLocations, zipcodeIds } = adgroupData.value;
    if (!particleLocations.length && !zipcodeIds.length) {
      return;
    }
    const locationTargetingAdContext = generateLocationTargetingAdContext({
      adgroupData: adgroupData.value,
      campaignData: campaignData.value,
    });
    const param = {
      ids: [...zipcodeIds, ...particleLocations],
      scene: LocationTargetingScene.GEO,
      adContext: locationTargetingAdContext,
    };
    const { tags, resp_context: respContext } = await run(param);
    const { associate_tags: associateTags } = respContext;

    const { formatResult, formatResultMap } = formatLocationItem({
      tags: [...tags, ...associateTags],
      isNotNeedFormatAssociateTags: true,
    });

    updateLocationData({
      list: formatResult,
      map: formatResultMap,
    });
  }

  return {
    getLocationListInfo,
  };
}

export function useLocationChildrenMerger({ areaMap }) {
  function handleWideRangeToSmallRange(opt: { newIds: number[]; preIds: number[] }) {
    const { newIds, preIds } = opt;
    let preSelectedIds = cloneDeep(preIds);
    // pre 大范围-> new 小范围
    newIds.forEach(id => {
      const newIdsParentId = [];
      let item = areaMap.value[id];
      while (item?.level && item?.level !== LocationGeoType.COUNTRY) {
        newIdsParentId.push(item?.parentId);
        item = areaMap.value[item?.parentId];
      }
      // 取之前选择的id和新选择的location的parentId的交集
      const commonIds = intersection(preSelectedIds, newIdsParentId);
      // 移除之前选择的id中的parentId
      preSelectedIds = difference(preSelectedIds, commonIds);
    });
    return { preSelectedIds };
  }

  function handleSmallRangeToWideRange(opt: { preIds: number[]; newIds: number[] }) {
    const { newIds, preIds } = opt;
    const preSelectedIds = cloneDeep(preIds);
    const newIdsSnapshot = cloneDeep(newIds);
    // pre 小范围-> new 大范围
    preIds.forEach(id => {
      const preIdsParentId = [];
      let item = areaMap.value[id];
      while (item?.level && item?.level !== LocationGeoType.COUNTRY) {
        preIdsParentId.push(item?.parentId);
        item = areaMap.value[item?.parentId];
      }
      // 新选择的id 是否有 之前选择的id的parentId
      const commonIds = intersection(newIdsSnapshot, preIdsParentId);
      if (commonIds.length) {
        const idIndex = preSelectedIds.findIndex(newId => newId === id);
        if (idIndex > -1) {
          preSelectedIds.splice(idIndex, 1);
        }
      }
    });

    return { newIdsSnapshot, preSelectedIds };
  }

  function handleHierarchicalRelationship(newIds: number[]) {
    const newIdsSnapshot = cloneDeep(newIds);
    const hashSelectedIds = [];
    newIds.forEach(id => {
      let parentId = areaMap.value[id]?.parentId;
      let parentItem = areaMap.value[parentId];
      while (parentItem) {
        if (newIdsSnapshot.includes(parentId)) {
          hashSelectedIds.push(id);
          break;
        }
        parentId = areaMap.value[parentId]?.parentId;
        parentItem = areaMap.value[parentId];
      }
    });
    return difference(newIdsSnapshot, hashSelectedIds);
  }

  function handleConflictDetection(opt: { newIds?: number[]; preIds?: number[]; deletedIds?: number[] }) {
    const { newIds, deletedIds } = opt;
    let { preIds } = opt;
    preIds = difference(preIds, deletedIds);
    const wideRangeToSmallRangeResult = handleWideRangeToSmallRange({
      preIds,
      newIds,
    });
    const smallRangeToWideRangeResult = handleSmallRangeToWideRange({
      preIds,
      newIds,
    });
    const preIdsSnapshot = intersection(
      wideRangeToSmallRangeResult.preSelectedIds,
      smallRangeToWideRangeResult.preSelectedIds,
    );
    const { newIdsSnapshot } = smallRangeToWideRangeResult;
    const zipcodeIds = [];
    const particleLocations = [];
    const allIds = [...preIdsSnapshot, ...newIdsSnapshot];
    allIds.forEach(id => {
      const location = areaMap.value[id];
      if (location?.level === LocationGeoType.ZIP_CODE) {
        zipcodeIds.push(id);
      } else {
        particleLocations.push(id);
      }
    });

    return {
      zipcodeIdsAfterResolveConflict: uniq(zipcodeIds),
      particleLocationsAfterResolveConflict: uniq(particleLocations),
    };
  }

  function handleVirtualNode({
    node,
    nodeIds,
    isChecked,
    virtualNodeIdArr,
    virtualNodeIdParentIdMap,
    metroIdsMap,
    regionIdsMap,
  }) {
    let checkedList = nodeIds;
    /** 判断是否选中了虚拟结点 */
    const isHasVirtualNodeId = Boolean(intersection(checkedList, virtualNodeIdArr.value).length);
    /** 选中了虚拟节点，则过滤掉 */
    if (isHasVirtualNodeId) {
      checkedList = checkedList.filter(item => !virtualNodeIdArr.value.includes(item));
    }
    /** 如果更新的 node 是选中 则选中其父节点 */
    if (isHasVirtualNodeId && isChecked) {
      const isCurNodeVirtual = virtualNodeIdArr.value.includes(node.id);
      const newCountryId = isCurNodeVirtual ? virtualNodeIdParentIdMap.value[node.id] : node.parentId;
      checkedList = checkedList.filter(
        item => !metroIdsMap.value[newCountryId]?.includes(item) && !regionIdsMap.value[newCountryId]?.includes(item),
      );
      checkedList.push(newCountryId);
    }
    checkedList = checkedList.filter(item => !String(item).startsWith('others'));
    return checkedList;
  }
  return { handleHierarchicalRelationship, handleConflictDetection, handleVirtualNode };
}

export function usePropsJapanVideoTipsVisible(opt: { adgroupData: Ref<AdgroupFormData> }) {
  const { fullCountryIds } = useStdLocation({
    adgroupData: opt.adgroupData,
  });
  const japanVideoTipsVisible = computed(() => fullCountryIds.value.includes(REGION_ID.JAPAN));
  return {
    japanVideoTipsVisible,
  };
}
